// Utilities → Tax Savings Report (Philip 2026-10-29): one property, one value year, one level of appeal — the client
// document laid out like the Huxley sample (docs/reference/Property Tax Savings Report - 1.pdf).
//
// ⛔ THE FIGURES ARE THE REPORT ENGINE'S (parcelYearRows), never a second derivation: a parcel's Savings here is its
// Savings in the Property Executive Summary, to the cent — the same representation gate, pro-rata share, Non-NTRG
// start and bill rules. This file only lays them out: market values, the row's rate, taxes at that rate, totals.
//
// Pure — no fetch. The page fetches the scope once (fetchParcelYearScope) and rebuilds from it when the level changes.

import { buildParcelYearRows } from './parcelYearRows';
import { toNumber, rawValue, isEstimated, isEffective, isRecovered, PENDING, EFFECTIVE_FOOTNOTE, RECOVERED_FOOTNOTE } from './format';
import { levelOrdinal } from '../utils/finalLevel';
import { prorataShares } from '../utils/prorata';

export const BASE_FOOTNOTE = 'Proposed and Final Taxes calculated using the displayed tax rate; figures with an asterisk are using prior year information.';
export const DERIVED_FOOTNOTE = '‡ rate derived from the savings calculation (e.g. where only part of the value is affected, or the bill rate applies to an equalized assessment).';

// The bill's rate stands when taxable × rate reproduces the engine's Savings within this; otherwise the row shows the
// rate the saving was figured at (Savings ÷ taxable reduction), marked ‡ (Philip, 2026-10-29).
const RECONCILE_TOLERANCE = 1;

const LEVELS = ['proposed', 'appeal_one', 'appeal_two', 'appeal_three', 'appeal_four'];

// "Proposed", "Appeal 1", "Appeal 2" … — the sample's wording, not the Level Names table (Philip, 2026-10-29).
export const reportLevelLabel = (level) => {
  const n = levelOrdinal(level);
  return n === 0 ? 'Proposed' : n > 0 ? `Appeal ${n}` : '';
};

// The levels this property has values at in the year (from the engine's per-level columns), and the default: the
// deepest level a decided parcel ended at, else the deepest level with values.
export function levelChoices(rows) {
  const present = LEVELS.filter((L) => rows.some((r) => r[`lvl_${L}_value`] !== undefined));
  const decided = rows.filter((r) => r.appeal_level_key && rawValue(r.savings) !== PENDING).map((r) => r.appeal_level_key);
  const deepest = (ls) => ls.reduce((a, l) => (levelOrdinal(l) > levelOrdinal(a) ? l : a), ls[0]);
  return { levels: present, defaultLevel: decided.length ? deepest(decided) : present.length ? deepest(present) : 'proposed' };
}

// This property's engine rows for the year: its parcels' rows and pro-rata shares counted toward it, baseline years
// out (not represented — "every represented parcel"). → { taxable, market } matched by row key.
export function propertyRows(raw, { propertyId, level }) {
  const build = (basis) => buildParcelYearRows(raw, { filters: { basis, throughLevel: level || null } })
    .filter((r) => r.property_id === propertyId && !r.baseline);
  const taxable = build('taxable');
  const market = new Map(build('market').map((r) => [r.__key, r]));
  return { taxable, market };
}

// One table row from the engine's taxable-basis row and its market-basis twin.
export function reportRow(t, m) {
  const tP = toNumber(t.proposed_value), tF = toNumber(t.final_value);
  const mvP = toNumber(m?.proposed_value), mvF = toNumber(m?.final_value);
  const pending = rawValue(t.savings) === PENDING;
  const savings = pending ? null : toNumber(t.savings);
  const estimated = isEstimated(t.taxes) || isEstimated(t.savings);
  const billRate = toNumber(t.rate);
  // A Cook bill's rate applies to the EQUALIZED value: taxes = taxable × equalizer × rate (1 where there is none).
  const eq = toNumber(t.equalizer) || 1;
  const red = tP != null && tF != null ? tP - tF : null;
  const viaBill = billRate != null && red != null ? red * eq * billRate / 100 : null;
  const derived = savings != null && red != null && red > 0 && (viaBill == null || Math.abs(viaBill - savings) > RECONCILE_TOLERANCE);
  const rate = derived ? (savings / (red * eq)) * 100 : billRate;
  return {
    key: t.__key,
    parcel_id: t.parcel_id,
    parcel: t.account_number || '',
    levelKey: t.appeal_level_key || null,   // the level the row is read at (the packet ticks that level's uploads)
    pro_rata_pct: t.pro_rata_pct ?? null,
    proposed_mv: mvP,
    final_mv: mvF,
    mv_reduction: pending ? PENDING : mvP != null && mvF != null ? mvP - mvF : null,
    rate,
    proposed_taxes: rate != null && tP != null ? tP * eq * rate / 100 : null,
    final_taxes: rate != null && tF != null ? tF * eq * rate / 100 : null,
    equalizer: toNumber(t.equalizer),
    savings: pending ? PENDING : savings,
    proposed_taxable: tP,
    final_taxable: tF,
    marks: {
      estimated: estimated && rate != null,
      derived,
      effective: !derived && isEffective(t.rate),
      recovered: !derived && isRecovered(t.rate),
    },
    assessor: t.assessor || '',
  };
}

// A represented parcel of the property with no figures that year still gets its line (blank cells), unless a pro-rata
// split sends it elsewhere.
const blankRow = (parcel) => ({
  key: `blank|${parcel.id}`, parcel_id: parcel.id, parcel: parcel.account_number || parcel.parcel_name || '', levelKey: null, pro_rata_pct: null,
  proposed_mv: null, final_mv: null, mv_reduction: null, rate: null, proposed_taxes: null, final_taxes: null, savings: null,
  proposed_taxable: null, final_taxable: null, equalizer: null, marks: {}, assessor: '',
});

const sum = (rows, k) => {
  const ns = rows.map((r) => r[k]).filter((v) => typeof v === 'number');
  return ns.length ? ns.reduce((a, b) => a + b, 0) : null;
};
const round6 = (v) => (v == null ? null : Math.round(v * 1e6) / 1e6);

// PROPERTY TOTAL: sums; the rate is the parcels' single rate when they all share it, else total final taxes ÷ total
// final taxable. All parcels pending → Pending.
export function totalRow(rows) {
  const priced = rows.filter((r) => r.rate != null);
  const rates = [...new Set(priced.map((r) => round6(r.rate)))];
  const shared = rates.length === 1 && priced.length === rows.filter((r) => r.final_taxable != null).length;
  const both = priced.filter((r) => r.final_taxable != null && r.final_taxable > 0);
  const ft = sum(both, 'final_taxes'), fx = sum(both.map((r) => ({ v: r.final_taxable * (r.equalizer || 1) })), 'v');   // the equalized value
  const allPending = rows.length > 0 && rows.every((r) => r.savings === PENDING);
  const markOf = (k) => rows.some((r) => r.marks[k]);
  return {
    proposed_mv: sum(rows, 'proposed_mv'),
    final_mv: sum(rows, 'final_mv'),
    mv_reduction: allPending ? PENDING : sum(rows, 'mv_reduction'),
    rate: shared ? priced[0].rate : ft != null && fx ? (ft / fx) * 100 : null,
    proposed_taxes: sum(rows, 'proposed_taxes'),
    final_taxes: sum(rows, 'final_taxes'),
    savings: allPending ? PENDING : sum(rows, 'savings'),
    marks: {
      estimated: markOf('estimated'),
      ...(shared ? { derived: priced[0].marks.derived, effective: priced[0].marks.effective, recovered: priced[0].marks.recovered } : {}),
    },
  };
}

const pctText = (v) => {
  const n = toNumber(v);
  if (n == null) return '';
  const pct = n <= 1 ? n * 100 : n;   // stored as 0.35 or as 35
  return `${Math.round(pct * 100) / 100}%`;
};

// The property types a report is run for (Philip 2026-10-29). Blank is Real Estate, as on the parcel card.
export const PROPERTY_TYPES = [{ key: 'RE', label: 'Real Estate' }, { key: 'BPP', label: 'Business Personal Property' }];
const typeOf = (parcel) => (String(parcel?.re_pp || '').trim().toUpperCase() === 'PERSONAL PROPERTY' ? 'BPP' : 'RE');

// Every represented parcel of the property that year, read up to `level`, each with its type and whether it has any
// value at that level or an earlier one (a parcel without is offered unticked, noted "no values"). Sorted by parcel.
export function reportCandidates(raw, { propertyId, year, level }) {
  const y = String(year);
  const { taxable, market } = propertyRows(raw, { propertyId, level });
  const rows = taxable.map((t) => reportRow(t, market.get(t.__key)));
  const shown = new Set(rows.map((r) => r.parcel_id));
  const allowed = raw.allowed?.get(y) || new Set();
  (raw.parcels || [])
    .filter((p) => p.property_id === propertyId && allowed.has(p.id) && !shown.has(p.id))
    .filter((p) => { const s = prorataShares(raw.prorata, p, y); return !s || s.some((x) => x.property_id === propertyId); })
    .forEach((p) => rows.push(blankRow(p)));
  const parcelById = new Map((raw.parcels || []).map((p) => [p.id, p]));
  return rows
    .map((r) => ({ ...r, type: typeOf(parcelById.get(r.parcel_id)),
      hasValues: [r.proposed_mv, r.final_mv, r.proposed_taxable, r.final_taxable].some((v) => v != null) }))
    .sort((a, b) => a.parcel.localeCompare(b.parcel, undefined, { numeric: true }));
}

// Everything the document prints. raw = fetchParcelYearScope for the client and year. type: 'RE' | 'BPP'.
// included: the candidate keys to print (default: those with values). Boxes and total cover the printed parcels only.
export function buildTaxSavingsReport(raw, { client, property, year, level, type = 'RE', included = null }) {
  const y = String(year);
  const candidates = reportCandidates(raw, { propertyId: property.id, year: y, level }).filter((r) => r.type === type);
  const rows = candidates.filter((r) => (included ? included.has(r.key) : r.hasValues));

  // Assessor and its RE / PP ratio for the year, over every printed parcel; several → each named once / "Varies".
  const resolved = raw.assessorByYear?.get(y)?.byParcelId;
  const assessors = [], ratios = [];
  rows.forEach((r) => {
    const a = resolved?.get(r.parcel_id);
    if (a?.name && !assessors.includes(a.name)) assessors.push(a.name);
    const ratio = pctText(a?.row ? (type === 'BPP' ? a.row.pp_ratio : a.row.re_ratio) ?? a.row.assessment_ratio : null);
    if (ratio && !ratios.includes(ratio)) ratios.push(ratio);
  });

  const total = totalRow(rows);
  const any = (k) => rows.some((r) => r.marks[k]);
  const equalizers = [...new Set(rows.map((r) => r.equalizer).filter((v) => v != null))];
  const address = [property.address, property.city, [property.state, property.postal_code].filter(Boolean).join(' ')]
    .map((s) => String(s || '').trim()).filter(Boolean).join(', ');
  return {
    title: `${y} PROPERTY TAX SAVINGS REPORT`,
    propertyName: property.name || '',
    client: client?.name || '',
    address,
    assessor: assessors.join('; '),
    ratio: ratios.length > 1 ? 'Varies' : ratios[0] || '',
    equalizer: equalizers.length > 1 ? 'Varies' : equalizers.length ? equalizers[0].toFixed(4) : '',   // printed only when one applies
    levelLabel: reportLevelLabel(level),
    year: y,
    rows,
    of: { included: rows.length, total: candidates.length },
    total,
    boxes: { mvReduction: total.mv_reduction, savings: total.savings, parcels: rows.length },
    footnotes: [
      BASE_FOOTNOTE,
      ...(any('derived') ? [DERIVED_FOOTNOTE] : []),
      ...(any('effective') ? [EFFECTIVE_FOOTNOTE] : []),
      ...(any('recovered') ? [RECOVERED_FOOTNOTE] : []),
      ...(rows.length < candidates.length ? [`Report includes ${rows.length} of ${candidates.length} parcels.`] : []),
    ],
  };
}

// ── display ───────────────────────────────────────────────────────────────────────────────────────────────────────
const marksText = (m = {}) => `${m.estimated ? '*' : ''}${m.recovered ? '†' : ''}${m.effective || m.derived ? '‡' : ''}`;
const num = (v, dp) => (v == null ? '' : v === PENDING ? PENDING
  : `$${(Math.round((v + Number.EPSILON) * 10 ** dp) / 10 ** dp).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
export const fmtValue = (v) => num(v, 0);
export const fmtMoney = (v, est) => { const s = num(v, 2); return s && s !== PENDING && est ? `${s}*` : s; };
export const fmtRate = (v, marks) => (v == null ? '' : `${v.toFixed(6)}${marksText(marks)}`);
